import React, { useEffect, useMemo, useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { useLocation, useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { useTranslation } from "../i18n/I18nContext";
import { allocateStudents } from "../services/classroom";
import { listActiveStudents } from "../services/student";
import { listFrequencies } from "../services/teacher";
import TransferList from "./shared/TransferList";

// Dual-list ("transfer list") allocation screen — its own page rather than a
// modal, mirroring sge_infantil_web's src/pages/class/allocateStudent screen:
// unallocated students of the turma's own nível/curso on the left, allocated
// students on the right, moved between the two with the >> / << buttons.
// The classroom travels in via router state (there's no "get classroom by
// id" endpoint on the backend, only the list one), so this page can only be
// reached from ListaTurmasLayer's "Alocar Estudantes" action.
const AlocarEstudantesLayer = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const classroom = location.state?.classroom || null;

  const [registrations, setRegistrations] = useState([]);
  const [frequencies, setFrequencies] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [allocatedIds, setAllocatedIds] = useState(() => (classroom?.students || []).map((s) => s.id));
  const [searchLeft, setSearchLeft] = useState("");
  const [searchRight, setSearchRight] = useState("");
  const [selectedLeft, setSelectedLeft] = useState([]);
  const [selectedRight, setSelectedRight] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!classroom) {
      toast.error(t("turma.allocate.error.notFound"));
      navigate("/turmas/lista", { replace: true });
      return;
    }
    listActiveStudents(classroom.academicYear)
      .then((resp) => setRegistrations(resp.data || []))
      .catch(() => setRegistrations([]))
      .finally(() => setLoadingList(false));
    listFrequencies().then((resp) => setFrequencies(resp.data || [])).catch(() => setFrequencies([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const frequencyName = (id) => frequencies.find((f) => f.id === id)?.description || "-";

  const genderLabel = (value) => {
    const normalized = (value || "").toString().trim().toUpperCase();
    if (normalized.startsWith("M")) return t("global.male");
    if (normalized.startsWith("F")) return t("global.female");
    return "-";
  };

  // Pool of candidate students: active registrations for this academic year
  // whose level matches the turma's educationLevel, plus any already-allocated
  // student that no longer shows up in that pool (e.g. inactive meanwhile) —
  // kept so they don't silently vanish from the "Alocados" side.
  const pool = useMemo(() => {
    if (!classroom) return new Map();
    const map = new Map();
    registrations
      .filter(
        (r) =>
          String(r.corseId) === String(classroom.educationLevel) ||
          String(r.student?.level) === String(classroom.educationLevel),
      )
      .forEach((r) => {
        map.set(r.studentId, {
          id: r.studentId,
          name: r.student?.name || "",
          code: r.student?.code || "",
          gender: r.student?.sex || "",
        });
      });
    (classroom.students || []).forEach((s) => {
      if (!map.has(s.id)) map.set(s.id, { id: s.id, name: s.name, code: s.code, gender: s.gender });
    });
    return map;
  }, [registrations, classroom]);

  const allItems = useMemo(() => Array.from(pool.values()), [pool]);

  const unallocated = useMemo(() => {
    const term = searchLeft.trim().toLowerCase();
    return allItems.filter(
      (it) => !allocatedIds.includes(it.id) && (!term || it.name.toLowerCase().includes(term)),
    );
  }, [allItems, allocatedIds, searchLeft]);

  const allocated = useMemo(() => {
    const term = searchRight.trim().toLowerCase();
    return allItems.filter(
      (it) => allocatedIds.includes(it.id) && (!term || it.name.toLowerCase().includes(term)),
    );
  }, [allItems, allocatedIds, searchRight]);

  const toggleLeft = (id) =>
    setSelectedLeft((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));
  const toggleRight = (id) =>
    setSelectedRight((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));

  const selectAllLeft = () => {
    const ids = unallocated.map((it) => it.id);
    const allSelected = ids.length > 0 && ids.every((id) => selectedLeft.includes(id));
    setSelectedLeft(allSelected ? [] : ids);
  };

  const selectAllRight = () => {
    const ids = allocated.map((it) => it.id);
    const allSelected = ids.length > 0 && ids.every((id) => selectedRight.includes(id));
    setSelectedRight(allSelected ? [] : ids);
  };

  const moveRight = () => {
    setAllocatedIds((cur) => Array.from(new Set([...cur, ...selectedLeft])));
    setSelectedLeft([]);
  };

  const moveLeft = () => {
    setAllocatedIds((cur) => cur.filter((id) => !selectedRight.includes(id)));
    setSelectedRight([]);
  };

  const capacity = classroom?.maxStudents || 0;
  const overCapacity = capacity > 0 && allocatedIds.length > capacity;

  const handleCancel = () => navigate("/turmas/lista");

  const handleSave = async () => {
    if (!classroom) return;
    if (overCapacity) {
      toast.error(t("turma.allocate.error.capacity"));
      return;
    }
    if (allocatedIds.length === 0) {
      toast.error(t("turma.allocate.error.empty"));
      return;
    }
    setSaving(true);
    try {
      const payload = allocatedIds.map((id) => {
        const item = pool.get(id);
        return { id, name: item?.name || "", code: item?.code || "" };
      });
      await allocateStudents(classroom.id, payload);
      toast.success(t("turma.success.allocated"));
      navigate("/turmas/lista");
    } catch (err) {
      toast.error(t("turma.error.allocate"));
    } finally {
      setSaving(false);
    }
  };

  if (!classroom) return null;

  return (
    <div className='row gy-4'>
      <ToastContainer />

      <div className='col-12'>
        <div className='card radius-12'>
          <div className='card-body'>
            <div className='d-flex flex-wrap align-items-center justify-content-between gap-3 mb-24'>
              <div>
                <h6 className='mb-4'>
                  {t("turma.action.allocateStudents")} — {classroom.className}
                </h6>
                <p className='text-secondary-light mb-0 text-sm'>
                  {frequencyName(classroom.educationLevel)} · {classroom.shift || "-"} ·{" "}
                  {t("turma.field.academicYear")}: {classroom.academicYear} ·{" "}
                  {t("turma.field.maxStudents")}: {capacity || "-"}
                </p>
              </div>
              <button
                type='button'
                className='btn btn-outline-secondary d-flex align-items-center gap-2'
                onClick={handleCancel}
              >
                <Icon icon='ic:round-arrow-back' />
                {t("global.cancel")}
              </button>
            </div>

            {overCapacity && (
              <div className='alert alert-danger d-flex align-items-center gap-2 mb-24'>
                <Icon icon='mdi:alert-circle-outline' />
                {t("turma.allocate.error.capacity")}
              </div>
            )}

            <TransferList
              leftTitle={t("turma.allocate.unallocated")}
              rightTitle={t("turma.allocate.allocated")}
              leftItems={unallocated}
              rightItems={allocated}
              selectedLeft={selectedLeft}
              selectedRight={selectedRight}
              onToggleLeft={toggleLeft}
              onToggleRight={toggleRight}
              onSelectAllLeft={selectAllLeft}
              onSelectAllRight={selectAllRight}
              onMoveRight={moveRight}
              onMoveLeft={moveLeft}
              searchLeft={searchLeft}
              onSearchLeftChange={setSearchLeft}
              searchRight={searchRight}
              onSearchRightChange={setSearchRight}
              loading={loadingList}
              loadingText={t("global.loading")}
              emptyLeftText={t("turma.allocate.empty.unallocated")}
              emptyRightText={t("turma.allocate.empty.allocated")}
              selectAllLabel={t("turma.allocate.selectAll")}
              searchPlaceholder={t("turma.allocate.search.placeholder")}
              renderItemMeta={(item) => `${item.code || "-"} · ${genderLabel(item.gender)}`}
            />

            <div className='d-flex justify-content-end gap-2 mt-24'>
              <button type='button' className='btn btn-outline-secondary' onClick={handleCancel}>
                {t("global.cancel")}
              </button>
              <button type='button' className='btn btn-primary-600' onClick={handleSave} disabled={saving}>
                {saving ? (
                  t("global.loading")
                ) : (
                  <>
                    <i className='ri-save-line' /> {t("global.save")}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AlocarEstudantesLayer;
